
import { Union, Record } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { sampleValue, ofLocalDriver, ofFetchedBigs, ofFetchedWords, Window$_$reflection } from "./WaveSlice.js";
import { class_type, union_type, record_type, array_type, uint32_type, bool_type, int32_type } from "../../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { tryFind, add, empty } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { equals, compare } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { filter, length, fold, iterate, partition } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { dbg, error } from "../../Shared/Log.js";
import { FastData, FastBits } from "../../Simulator/SimGraphTypes.js";
import { toUInt32 } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";

/**
 * One wave's samples, and the window they cover.
 * 
 * Per WAVE, not per view. What the viewer needs to know is "does this wave have the cycles it is
 * being drawn over", and that is a question about one wave: a wave just added to the selection is
 * missing while every other wave is fine, and a window that has moved leaves them all missing
 * together. Keying by wave says both without a special case, where one entry for the whole view
 * could only say "all" or "none".
 * 
 * Waves fetched together share one array - a reply is signal-major and copying it apart would be
 * the only copy in the path - so each entry carries where its own row starts.
 */
export class CachedWave extends Record {
    constructor(Window$, Width, LeadIn, WordsPerSample, RowBase, Data) {
        super();
        this.Window = Window$;
        this.Width = (Width | 0);
        this.LeadIn = LeadIn;
        this.WordsPerSample = (WordsPerSample | 0);
        this.RowBase = (RowBase | 0);
        this.Data = Data;
    }
}

export function CachedWave_$reflection() {
    return record_type("WaveData.CachedWave", [], CachedWave, () => [["Window", Window$_$reflection()], ["Width", int32_type], ["LeadIn", bool_type], ["WordsPerSample", int32_type], ["RowBase", int32_type], ["Data", array_type(uint32_type)]]);
}

/**
 * What the cache holds for one wave over one window: its samples, or the reason there are none.
 * 
 * "There are none, and asking again will not help" has to be sayable. A missing wave is exactly
 * what asks for a fetch, so a wave that can never be fetched - the simulation has no driver to
 * name it by - would be asked for again by every update for as long as it stayed selected. The
 * answer is recorded like any other, against the window it was asked over.
 */
export class Held extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Samples", "NoDriver"];
    }
}

export function Held_$reflection() {
    return union_type("WaveData.Held", [], Held, () => [[["Item", CachedWave_$reflection()]], [["Item", Window$_$reflection()]]]);
}

function windowHeld(_arg) {
    if (_arg.tag === 1) {
        return _arg.fields[0];
    }
    else {
        return _arg.fields[0].Window;
    }
}

export class Source extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Local", "Fetched"];
    }
    static Local = new Source(0, []);
}

export function Source_$reflection() {
    return union_type("WaveData.Source", [], Source, () => [[], [["epoch", int32_type], ["Item2", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [int32_type, Held_$reflection()])]]]);
}

let source = Source.Local;

let localData = (_arg) => undefined;

let localClock = () => 0;

/**
 * Read from the renderer's own simulation, through `lookup`. Nothing is copied - a local slice
 * names the step array where it lies - so this "fill" is only recording how to find it.
 */
export function setLocal(lookup, clock) {
    localData = lookup;
    localClock = clock;
    source = Source.Local;
}

/**
 * Read from the sidecar session `epoch`, holding nothing yet: every wave has to be asked for.
 * 
 * NOT the same as Local, which is what the renderer's own simulation is. In .NET mode the
 * renderer's step arrays exist but are never run, so reading through to them would draw a column
 * of zeros with the confidence of simulation output. This says "ask", where Local says "look".
 */
export function holdNothing(epoch) {
    source = (new Source(/* Fetched */ 1, [epoch, empty({
        Compare: (x, y) => (compare(x, y) | 0),
    })]));
}

/**
 * Add what a fetch of session `epoch` carried, keeping any waves already held that it did not
 * carry - a fetch asks only for the waves that were missing, so the rest are still current.
 * 
 * **Refused unless the cache is of that session** (invariant D4). This is written from inside the
 * promise that fetched, where the model - and so which session is on screen - is not reachable;
 * the alternative was to write regardless and check somewhere later, and there is no later that
 * comes before the next render. A reply from a build that has been replaced would otherwise land
 * under driver indices the new build has reused, and be drawn under the new signal's name until
 * something moved.
 * 
 * A wave short of its own samples is reported AND kept out (invariant D3). A short reply is
 * silent: reading past the end of a typed array is `undefined` in JavaScript rather than a fault,
 * so every wave after the truncation point drew somebody else's samples - or NaNs - as
 * confidently as the rest. Reporting it and then storing it anyway made the check a description
 * of the failure instead of a stop to it. A wave with no entry draws nothing, which is what the
 * viewer already does for one whose data has not arrived.
 */
export function setFetched(epoch, waves) {
    let held;
    let matchResult, existing_1, held_1;
    if (source.tag === 1) {
        if ((held = (source.fields[0] | 0), (source.fields[1], held === epoch))) {
            matchResult = 0;
            existing_1 = source.fields[1];
            held_1 = source.fields[0];
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0: {
            const needsOf = (cached) => ((cached.RowBase + ((cached.Window.SampleCount + (cached.LeadIn ? 1 : 0)) * cached.WordsPerSample)) | 0);
            const patternInput = partition((tupledArg) => {
                const c = tupledArg[1];
                return c.Data.length >= needsOf(c);
            }, waves);
            iterate((tupledArg_1) => {
                const cached_1 = tupledArg_1[1];
                error(`waveform cache: wave ${tupledArg_1[0]} needs ${needsOf(cached_1)} values and the reply holds ${cached_1.Data.length}; it is left blank (invariant D3)`);
            }, patternInput[1]);
            source = (new Source(/* Fetched */ 1, [epoch, fold((m, tupledArg_2) => add(tupledArg_2[0], new Held(/* Samples */ 0, [tupledArg_2[1]]), m), existing_1, patternInput[0])]));
            break;
        }
        case 1: {
            dbg(64, `a fetch of session ${epoch} landed after the cache moved on; ${length(waves)} waves dropped (invariant D4)`);
            break;
        }
    }
}

/**
 * Record that these waves were asked for over this window under session `epoch`, and the
 * simulation cannot name them.
 * 
 * Not an error and not a gap to be retried: a wave whose driver the simulation does not offer is
 * one this build has no way of fetching, so what is recorded is that answer. It goes in the cache
 * rather than in a list of exceptions because the question - "has this wave got the window it is
 * drawn over" - is the same one, and one answer is easier to keep true than two.
 * 
 * Session-checked exactly as `setFetched` is, and for the same reason: "no driver" is an answer
 * about one build, and the next build may well have one.
 */
export function setNoDriver(epoch, handles, window$) {
    let held;
    let matchResult, existing_1, held_1;
    if (source.tag === 1) {
        if ((held = (source.fields[0] | 0), (source.fields[1], held === epoch))) {
            matchResult = 0;
            existing_1 = source.fields[1];
            held_1 = source.fields[0];
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0: {
            source = (new Source(/* Fetched */ 1, [epoch, fold((m, h) => add(h, new Held(/* NoDriver */ 1, [window$]), m), existing_1, handles)]));
            break;
        }
        case 1: {
            break;
        }
    }
}

export function current() {
    return source;
}

/**
 * Does this wave hold the window it is about to be drawn over?
 * 
 * The renderer's own simulation always does - it is read in place - so only a fetched source can
 * answer no, and a no is what makes a wave one that needs fetching.
 */
export function hasData(_arg, window$) {
    if (source.tag === 1) {
        const matchValue = tryFind(_arg, source.fields[1]);
        if (matchValue == null) {
            return false;
        }
        else {
            return equals(windowHeld(matchValue), window$);
        }
    }
    else {
        return true;
    }
}

/**
 * The window this wave's samples cover, when it has some.
 * 
 * What the viewer can draw for it RIGHT NOW, which while a view is being scrolled is neither the
 * window on screen nor the one the controls ask for: it is whatever the last fetch to land carried.
 * Drawing that rather than keeping what is on screen is what makes a fast scroll move.
 */
export function heldWindow(_arg) {
    if (source.tag === 1) {
        const matchValue = tryFind(_arg, source.fields[1]);
        let matchResult, c;
        if (matchValue == null) {
            matchResult = 1;
        }
        else if (matchValue.tag === 1) {
            matchResult = 1;
        }
        else {
            matchResult = 0;
            c = matchValue.fields[0];
        }
        switch (matchResult) {
            case 0:
                return c.Window;
            default:
                return undefined;
        }
    }
    else {
        return undefined;
    }
}

/**
 * The waves, of those being drawn, that do not hold the window they are drawn over.
 * 
 * Derived, every time it is asked for, from the cache and the view. Nothing records which waves
 * are outstanding: a wave needs fetching exactly when it has not got the cycles it is being drawn
 * over, and that is a question with an answer at any moment.
 */
export function needFetching(handles, window$) {
    return filter((h) => !hasData(h, window$), handles);
}

/**
 * A slice of one wave over `window`, or None where the data is not held - which for a fetched
 * source means the view has moved and the fetch for it has not landed yet, and for a local one
 * means the simulation has not run that far. Every caller already has a way of showing nothing.
 */
export function slice(h, window$) {
    const h_1 = h;
    if (source.tag === 1) {
        const matchValue = tryFind(h_1, source.fields[1]);
        let matchResult, c_1;
        if (matchValue != null) {
            if (matchValue.tag === 0) {
                if (equals(matchValue.fields[0].Window, window$)) {
                    matchResult = 0;
                    c_1 = matchValue.fields[0];
                }
                else {
                    matchResult = 1;
                }
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                if (c_1.Width <= 32) {
                    return ofFetchedWords(c_1.Data, c_1.RowBase, c_1.WordsPerSample, c_1.Width, window$, c_1.LeadIn);
                }
                else {
                    return ofFetchedBigs(c_1.Data, c_1.RowBase, c_1.WordsPerSample, c_1.Width, window$, c_1.LeadIn);
                }
            default:
                return undefined;
        }
    }
    else {
        const option_1 = localData(h_1);
        if (option_1 != null) {
            return ofLocalDriver(option_1, localClock(), window$);
        }
        else {
            return undefined;
        }
    }
}

/**
 * The value of one wave at one clock cycle, for the value column, the hover tooltip and the
 * schematic probe. None where it cannot be answered.
 */
export function valueAt(h, cycle) {
    let this$_2, width, v;
    const h_1 = h;
    if (source.tag === 1) {
        const matchValue = tryFind(h_1, source.fields[1]);
        let matchResult, c;
        if (matchValue != null) {
            if (matchValue.tag === 0) {
                matchResult = 1;
                c = matchValue.fields[0];
            }
            else {
                matchResult = 0;
            }
        }
        else {
            matchResult = 0;
        }
        switch (matchResult) {
            case 0:
                return undefined;
            default: {
                const offset = (cycle - ((this$_2 = c.Window, this$_2.StartSample * this$_2.Multiplier))) | 0;
                if ((offset < 0) ? true : ((offset % c.Window.Multiplier) !== 0)) {
                    return undefined;
                }
                else {
                    const i = ~~(offset / c.Window.Multiplier) | 0;
                    if (i >= c.Window.SampleCount) {
                        return undefined;
                    }
                    else {
                        return (width = (c.Width | 0), (v = sampleValue((c.Width <= 32) ? ofFetchedWords(c.Data, c.RowBase, c.WordsPerSample, c.Width, c.Window, c.LeadIn) : ofFetchedBigs(c.Data, c.RowBase, c.WordsPerSample, c.Width, c.Window, c.LeadIn), i), (width > 32) ? (new FastData(new FastBits(/* BigWord */ 1, [v]), width)) : (new FastData(new FastBits(/* Word */ 0, [toUInt32(v) >>> 0]), width))));
                    }
                }
            }
        }
    }
    else {
        let option_7;
        const option_1 = localData(h_1);
        option_7 = ((option_1 != null) ? ((cycle <= localClock()) ? option_1 : undefined) : undefined);
        if (option_7 != null) {
            const io = option_7;
            if (io.Width > 32) {
                let option_3;
                const this$ = io;
                const step = cycle | 0;
                option_3 = (((this$.BigIntSlab.length === 0) ? true : ((step >>> 0) >= (this$.StepLength >>> 0))) ? undefined : (this$.BigIntSlab[(this$.StepBase + step)]));
                if (option_3 != null) {
                    return new FastData(new FastBits(/* BigWord */ 1, [option_3]), io.Width);
                }
                else {
                    return undefined;
                }
            }
            else {
                let option_5;
                const this$_1 = io;
                const step_1 = cycle | 0;
                option_5 = (((this$_1.UInt32Slab.length === 0) ? true : ((step_1 >>> 0) >= (this$_1.StepLength >>> 0))) ? undefined : (this$_1.UInt32Slab[(this$_1.StepBase + step_1)]));
                if (option_5 != null) {
                    return new FastData(new FastBits(/* Word */ 0, [option_5]), io.Width);
                }
                else {
                    return undefined;
                }
            }
        }
        else {
            return undefined;
        }
    }
}

